import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import {
  Button,
  DateRangePicker,
  InfoBlock,
  Input,
  NumberInput,
  Pagination,
  SearchIcon,
  Table,
} from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { HeaderGroup } from 'react-table';

import { useSharedDateRange } from 'hooks';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { debounce, formatDateForServer } from 'utils';

import s from './Clients.module.scss';
import { getColumns } from './columns';
import {
  GENERATE_CLIENTS_REPORT,
  IClientReportResponse,
  IClientsReportRequest,
} from './schema';
import { ISort } from './types';

const COUNT_PER_PAGE = 50;

const DEFAULT_SORT = { id: 'total', desc: true };

export const Clients: FC = () => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const today = dayjs();
  const {
    startDate,
    endDate,
    periodType,
    offset,
    setStartDate,
    setEndDate,
    setPeriodType,
    setOffset,
  } = useSharedDateRange();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ISort>({
    field: DEFAULT_SORT.id,
    direction: DEFAULT_SORT.desc ? 'DESC' : 'ASC',
  });

  const [error, setError] = useState<string | null>(null);

  const [generateReport, { data: response, loading, variables }] = useLazyQuery<
    IClientReportResponse,
    IClientsReportRequest
  >(GENERATE_CLIENTS_REPORT, {
    fetchPolicy: 'no-cache',
    onError: (error) => {
      setError(error.message);
    },
  });

  const debouncedGenerateReport = useMemo(
    () => debounce(generateReport, 300),
    [generateReport]
  );

  useEffect(() => {
    if (!(search.length > 2 || search === '')) return;
    if (!variables?.input) return;

    debouncedGenerateReport({
      variables: {
        ...variables,
        input: {
          ...variables.input,
          search: search
            ? {
                q: search,
              }
            : undefined,
        },
      },
    });
  }, [search, debouncedGenerateReport]);

  useEffect(() => {
    if (!variables?.input) return;

    debouncedGenerateReport({
      variables: {
        ...variables,
        input: {
          ...variables.input,
          sorting: sort,
        },
      },
    });
  }, [sort, debouncedGenerateReport]);

  const tableData = response?.clientsPaymentReport.data ?? [];
  const paginatorInfo = response?.clientsPaymentReport.paginatorInfo ?? {
    currentPage: 1,
    lastPage: 1,
  };

  const COLUMNS = useMemo(() => getColumns(currency.sign, t), [t]);

  const { control, handleSubmit } = useForm<{ page: number }>();

  interface PageForm {
    page: number;
  }

  const onSubmitPage = handleSubmit((data: PageForm) => {
    const page = Math.max(1, Math.min(data.page || 1, paginatorInfo.lastPage));

    if (!variables) return;

    // Создаем новый объект переменных, гарантируя что все обязательные поля присутствуют
    const newVariables: IClientsReportRequest = {
      ...variables,
      page,
      input: variables.input || { from: '', to: '' }, // Предоставляем значения по умолчанию для обязательных полей
    };

    generateReport({ variables: newVariables });
  });
  return (
    <div className={s.container}>
      {error && (
        <InfoBlock title={t('errors.error')} message={error} direction="row" />
      )}
      <div className={s.generate}>
        <div className={s.dates}>
          <DateRangePicker
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            lastValidDate={today.format()}
            firstValidDate={dayjs('2022-03-01T00:00:00').format()}
            lang={lang}
            periodType={periodType}
            setPeriodType={setPeriodType}
            offset={offset}
            setOffset={setOffset}
          />
        </div>
        <Button
          className={s.generate_button}
          onClick={() => {
            setError(null);

            const input: IClientsReportRequest['input'] = {
              from: formatDateForServer(dayjs(startDate)),
              to: formatDateForServer(dayjs(endDate)),
            };

            if (search.length > 2) {
              input.search = {
                q: search,
              };
            }

            if (sort) {
              input.sorting = sort;
            }

            debouncedGenerateReport({
              variables: {
                first: COUNT_PER_PAGE,
                page: 1,
                input,
              },
            });
          }}
          disabled={loading}
        >
          {t('reports.generate')}
        </Button>
      </div>
      <div className={s.data}>
        <div className={s.topControls}>
          <Input
            leftContent={<SearchIcon color="primary" />}
            variant="secondary"
            placeholder={t('clients.search')}
            value={search}
            onChange={setSearch}
            errorWrapperClassName={s.search}
          />
          {paginatorInfo.lastPage > 1 && variables && (
            <div className={s.pagination__block}>
              <Pagination
                onChange={(page) => {
                  generateReport({ variables: { ...variables, page } });
                }}
                {...paginatorInfo}
                className={s.pagination}
                isChose={true}
              />
              <form onSubmit={onSubmitPage}>
                <Controller
                  name="page"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <NumberInput
                      value={
                        paginatorInfo.currentPage === 1
                          ? ''
                          : paginatorInfo.currentPage
                      }
                      onChange={(val) => {
                        if (typeof val === 'number') {
                          if (val < 1) {
                            onChange(1);
                          } else if (val > paginatorInfo.lastPage) {
                            onChange(paginatorInfo.lastPage);
                          } else {
                            onChange(val);
                          }
                        } else {
                          onChange('');
                        }
                      }}
                      variant="secondary"
                      placeholder="№"
                      className={s.pagination_input}
                    />
                  )}
                />
                <button type="submit" style={{ display: 'none' }} />
              </form>{' '}
            </div>
            // <Pagination
            //   onChange={(page) => {
            //     generateReport({ variables: { ...variables, page } });
            //   }}
            //   {...paginatorInfo}
            //   className={s['pagination']}
            // />
          )}
        </div>
        <Table
          data={tableData}
          columns={COLUMNS}
          isLoading={loading}
          sortBy={[DEFAULT_SORT]}
          onHeaderClick={(column: HeaderGroup) => {
            Promise.resolve().then(() => {
              const direction = column.isSortedDesc
                ? 'DESC'
                : column.isSorted
                ? 'ASC'
                : 'DESC';

              setSort({ field: column.id, direction });
            });
          }}
          emptyDataPlaceholder={t(
            variables ? 'report.placeholder' : 'report.placeholder.emptyDates'
          )}
          className={s.table}
          manualSortBy
          bigRows
        />
      </div>
    </div>
  );
};
